import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Container,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { motion } from 'framer-motion';

const AboutSection: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';

  // Theme-aware overlays - black and white
  const overlayGradient = isDark
    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.05) 100%)'
    : 'linear-gradient(135deg, rgba(0, 0, 0, 0.02) 0%, rgba(0, 0, 0, 0.02) 100%)';
  const cardBg = isDark
    ? 'linear-gradient(135deg, rgba(26,26,26,0.98) 0%, rgba(34,34,34,0.98) 100%)'
    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.95) 100%)';
  const cardBorder = isDark
    ? '1px solid rgba(255,255,255,0.08)'
    : '1px solid rgba(0, 0, 0, 0.1)';

  // AboutSection: bubble state
  const [showSmpBubble, setShowSmpBubble] = useState(false);

  return (
    <Box
      id="iam"
      sx={{
        py: { xs: 4, sm: 6, md: 8 },
        backgroundColor: theme.palette.background.default,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          background: overlayGradient,
          zIndex: 0,
        },
        '@keyframes sparkle': {
          '0%, 100%': { opacity: 0 },
          '50%': { opacity: 0.8 },
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
          pointerEvents: 'none',
          backgroundImage: `
            radial-gradient(circle, rgba(0,0,0,0.1) 1px, transparent 1px),
            radial-gradient(circle, rgba(0,0,0,0.1) 1px, transparent 1px),
            radial-gradient(circle, rgba(0,0,0,0.05) 1px, transparent 1px)
          `,
          backgroundRepeat: 'repeat',
          backgroundSize: {
            xs: '100px 100px, 200px 200px, 300px 300px',
            md: '200px 200px, 300px 300px, 400px 400px',
          },
          backgroundPosition: '0 0, 50px 50px, 100px 100px',
          animation: `sparkle 15s linear infinite`,
          opacity: 0,
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: 'easeOut' }}
          viewport={{ once: true }}
        >
          <Typography
            variant={isMobile ? 'h3' : 'h2'}
            component="h2"
            align="center"
            className="section-heading-allura"
            sx={{ 
              mb: { xs: 4, sm: 6, md: 8 }, 
              fontWeight: 400,
              letterSpacing: '0.02em',
              lineHeight: 1.1,
              position: 'relative',
              fontFamily: 'Allura, cursive !important',
              fontSize: { xs: '36px !important', sm: '42px !important', md: '48px !important' },
              color: theme.palette.text.primary,
              '@media (min-width: 480px) and (max-width: 600px)': {
                fontSize: '48px !important',
              },
              '@media (min-width: 601px) and (max-width: 960px)': {
                fontSize: '56px !important',
              },
              '@media (min-width: 961px) and (max-width: 1280px)': {
                fontSize: '64px !important',
              },
              '@media (min-width: 1281px)': {
                fontSize: '72px !important',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -16,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 80,
                height: 4,
                background: theme.palette.text.primary,
                borderRadius: 2,
              },
            }}
          >
            I am..
          </Typography>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, delay: 0.2, ease: 'easeOut' }}
          viewport={{ once: true }}
        >
          <Card 
            sx={{ 
              maxWidth: 800,
              mx: 'auto',
              background: cardBg,
              backdropFilter: 'blur(20px)',
              border: cardBorder,
            }}
          >
            <CardContent sx={{ p: { xs: 3, sm: 4, md: 6 } }}>
              <Typography
                variant="h4"
                component="h3"
                sx={{ 
                  mb: 4, 
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                  textAlign: 'center',
                  fontFamily: '"Roboto Mono", "Courier New", monospace',
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                  '@media (min-width:601px) and (max-width:960px)': {
                    fontSize: '1.625rem',
                    lineHeight: 1.35,
                  },
                }}
              >
                Technology & Innovation Leader
              </Typography>
              <Typography
                variant="body1"
                sx={{ 
                  mb: 4, 
                  letterSpacing: '0.009em',
                  color: theme.palette.text.secondary,
                  fontFamily: '"Roboto Mono", "Courier New", monospace',
                  fontSize: { xs: '0.9375rem', sm: '1rem', md: '1.125rem' },
                  lineHeight: { xs: 1.6, sm: 1.7, md: 1.8 },
                  '@media (min-width:601px) and (max-width:960px)': {
                    fontSize: '1.0625rem',
                    lineHeight: 1.7,
                  },
                }}
              >
                With 21+ years at the forefront of <strong style={{ color: theme.palette.text.primary, fontWeight: 700 }}>Cloud Computing</strong>, <strong style={{ color: theme.palette.text.primary, fontWeight: 700 }}>AI/ML</strong>, <strong style={{ color: theme.palette.text.primary, fontWeight: 700 }}>Risk, FinOps</strong>, and <strong style={{ color: theme.palette.text.primary, fontWeight: 700 }}>digital innovation</strong>, I drive transformative solutions that fuel business growth and operational excellence. I have led impactful digital transformations across <strong style={{ color: theme.palette.text.primary, fontWeight: 700 }}>Banking, Financial Services, Insurance (BFSI)</strong>, <strong style={{ color: theme.palette.text.primary, fontWeight: 700 }}>Retail</strong>, and <strong style={{ color: theme.palette.text.primary, fontWeight: 700 }}>Cryptocurrency</strong> domains, delivering scalable and future-ready systems. My journey spans <strong style={{ color: theme.palette.text.primary, fontWeight: 700 }}>Ireland, Canada, China, Hong Kong, and Germany</strong>, equipping me with a global perspective on technology leadership and cultural adaptability.
              </Typography>
              <Typography
                variant="body1"
                sx={{ 
                  mb: 4, 
                  letterSpacing: '0.009em',
                  color: theme.palette.text.secondary,
                  fontFamily: '"Roboto Mono", "Courier New", monospace',
                  fontSize: { xs: '0.9375rem', sm: '1rem', md: '1.125rem' },
                  lineHeight: { xs: 1.6, sm: 1.7, md: 1.8 },
                  '@media (min-width:601px) and (max-width:960px)': {
                    fontSize: '1.0625rem',
                    lineHeight: 1.7,
                  },
                }}
              >
                Successfully led <strong style={{ color: theme.palette.text.primary, fontWeight: 700 }}>digital transformation</strong> initiatives across <strong style={{ color: theme.palette.text.primary, fontWeight: 700 }}>Banking, Financial Services, Insurance (BFSI)</strong>, <strong style={{ color: theme.palette.text.primary, fontWeight: 700 }}>Retail</strong>, and <strong style={{ color: theme.palette.text.primary, fontWeight: 700 }}>Cryptocurrency</strong> domains, delivering innovative solutions that drive business growth and operational efficiency.
              </Typography>
              <Typography
                variant="body1"
                sx={{ 
                  letterSpacing: '0.009em',
                  color: theme.palette.text.secondary,
                  fontFamily: '"Roboto Mono", "Courier New", monospace',
                  fontSize: { xs: '0.9375rem', sm: '1rem', md: '1.125rem' },
                  lineHeight: { xs: 1.6, sm: 1.7, md: 1.8 },
                  '@media (min-width:601px) and (max-width:960px)': {
                    fontSize: '1.0625rem',
                    lineHeight: 1.7,
                  },
                }}
              >
                Currently completing the prestigious 
                <span style={{ position: 'relative', display: 'inline-block' }}>
                  <a
                    href="https://medium.com/@priyakantcharokar/iimc-smp-batch-23-revision-notes-6ff59af63893"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: theme.palette.text.primary, fontWeight: 700, fontSize: isMobile ? '1.05rem' : '1.15rem', textDecoration: 'underline dotted' }}
                    onMouseEnter={() => setShowSmpBubble(true)}
                    onMouseLeave={() => setShowSmpBubble(false)}
                    onFocus={() => setShowSmpBubble(true)}
                    onBlur={() => setShowSmpBubble(false)}
                    onTouchStart={() => setShowSmpBubble(v => !v)}
                  >
                    Senior Management Programme (SMP) at IIM Calcutta
                  </a>
                  {showSmpBubble && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.7 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.7 }}
                      transition={{ duration: 0.28, ease: 'easeOut' }}
                      whileHover={{ scale: 1.04, boxShadow: '0 8px 32px 0 rgba(0,0,0,0.18)' }}
                      style={{
                        position: 'absolute',
                        left: isMobile ? '0' : '100%',
                        top: isMobile ? '-60px' : '0',
                        zIndex: 2000,
                        minWidth: isMobile ? 120 : 180,
                        maxWidth: isMobile ? 320 : 420,
                        padding: isMobile ? '12px 18px' : '16px 28px',
                        fontSize: isMobile ? '0.92rem' : '1.05rem',
                        fontFamily: 'Allura, cursive',
                        fontWeight: 400,
                        color: '#222',
                        background: '#fffbe6',
                        borderRadius: 6,
                        boxShadow: '0 6px 32px 0 rgba(255, 193, 7, 0.18)',
                        border: 'none',
                        display: 'block',
                        pointerEvents: 'auto',
                        transition: 'all 0.2s',
                        whiteSpace: 'normal',
                        overflow: 'visible',
                        animation: 'calloutPulse 1.2s infinite',
                        transform: isMobile ? 'rotate(-2deg)' : 'translateY(0) rotate(-3deg)',
                        marginLeft: isMobile ? 0 : 18,
                        marginTop: isMobile ? 0 : 0,
                        lineHeight: 1.5,
                        wordBreak: 'break-word',
                      }}
                    >
                        <div style={{
                          textAlign: 'center',
                          fontWeight: 700,
                          fontSize: isMobile ? '0.92rem' : '1rem',
                          marginBottom: 2,
                          letterSpacing: '0.01em',
                        }}>
                          IIMc SMP
                        </div>
                        <div style={{
                          textAlign: 'center',
                          fontWeight: 400,
                          fontSize: isMobile ? '0.82rem' : '0.92rem',
                          color: '#444',
                          lineHeight: 1.3,
                          letterSpacing: '0.01em',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                          Notes Hub! 📚✨
                        </div>
                    </motion.div>
                  )}
                </span>
                , a transformative journey shaping experienced professionals into strategic, future-ready leaders. This program is sharpening my ability to drive change at the top, enhancing my <strong style={{ color: theme.palette.text.primary, fontWeight: 700 }}>strategic insight</strong>, <strong style={{ color: theme.palette.text.primary, fontWeight: 700 }}>leadership influence</strong>, and <strong style={{ color: theme.palette.text.primary, fontWeight: 700 }}>decision-making skills</strong> to lead confidently in complex business environments.
              </Typography>
            </CardContent>
          </Card>
        </motion.div>
      </Container>
    </Box>
  );
};

export default AboutSection; 